/**
 * TrialBanner — top-of-page banner stack showing active module trials and expiry.
 *
 * Behaviour:
 *   - Pulls active trials from useModules() (sourced from /auth/me -> moduleTrials).
 *   - One card per active trial, color-coded by urgency:
 *       red    : ≤ 3 days remaining  (critical, cannot be dismissed)
 *       amber  : ≤ 7 days remaining  (warning, dismissable for the session)
 *       blue   : > 7 days remaining  (informational, dismissable for the session)
 *   - Dismissals are stored in sessionStorage keyed by code+expiresAt so a
 *     refresh keeps it dismissed but a new trial period (or fresh login) resurfaces it.
 *   - Critical (≤3d) trials always show even if the user dismissed them earlier —
 *     this is the "expires soon" reminder.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useModules } from '../modules/useModules';
import { MODULES_BY_CODE } from '../modules/catalog';

const STORAGE_PREFIX = 'trialBannerDismissed:';

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function styleFor(daysRemaining) {
  if (daysRemaining <= 3) {
    return {
      severity: 'critical',
      container: 'bg-red-50 border-red-300 text-red-900',
      pill: 'bg-red-600 text-white',
      icon: '⚠️',
      cta: 'Expires very soon — contact your admin to keep this module active.',
    };
  }
  if (daysRemaining <= 7) {
    return {
      severity: 'warning',
      container: 'bg-amber-50 border-amber-300 text-amber-900',
      pill: 'bg-amber-500 text-white',
      icon: '⏳',
      cta: 'Trial ending soon — talk to your admin about converting.',
    };
  }
  return {
    severity: 'info',
    container: 'bg-blue-50 border-blue-300 text-blue-900',
    pill: 'bg-blue-600 text-white',
    icon: '🎁',
    cta: 'Enjoy your free trial — convert any time to keep access.',
  };
}

function TrialCard({ trial, onDismiss }) {
  const meta = MODULES_BY_CODE[trial.code] || { name: trial.code, icon: '🧩' };
  const days = Number(trial.daysRemaining ?? 0);
  const s = styleFor(days);
  const canDismiss = s.severity !== 'critical';

  return (
    <div className={`flex items-start gap-3 border rounded-lg px-4 py-3 shadow-sm ${s.container}`}>
      <div className="text-2xl leading-none">{meta.icon || s.icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{meta.name} trial active</span>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.pill}`}>
            {days <= 0 ? 'Last day' : `${days} day${days === 1 ? '' : 's'} left`}
          </span>
          <span className="text-xs opacity-80">expires {formatDate(trial.expiresAt)}</span>
        </div>
        <div className="text-sm mt-0.5 opacity-90">{s.cta}</div>
      </div>
      {canDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="text-xl leading-none opacity-60 hover:opacity-100"
          aria-label="Dismiss"
          title="Dismiss for this session"
        >
          ×
        </button>
      )}
    </div>
  );
}

export default function TrialBanner() {
  const { trials } = useModules();
  const [dismissed, setDismissed] = useState({});

  // Load dismissed state from sessionStorage on mount
  useEffect(() => {
    try {
      const next = {};
      for (const t of trials || []) {
        const key = STORAGE_PREFIX + t.code + ':' + (t.expiresAt || '');
        if (sessionStorage.getItem(key) === '1') next[t.code] = true;
      }
      setDismissed(next);
    } catch {
      /* ignore storage errors */
    }
  }, [trials]);

  const visible = useMemo(() => {
    return (trials || []).filter(t => {
      const days = Number(t.daysRemaining ?? 0);
      // Critical reminders always show
      if (days <= 3) return true;
      return !dismissed[t.code];
    });
  }, [trials, dismissed]);

  if (!visible.length) return null;

  const dismiss = (trial) => {
    try {
      const key = STORAGE_PREFIX + trial.code + ':' + (trial.expiresAt || '');
      sessionStorage.setItem(key, '1');
    } catch {
      /* ignore */
    }
    setDismissed(prev => ({ ...prev, [trial.code]: true }));
  };

  return (
    <div className="px-6 pt-4 space-y-2">
      {visible.map(t => (
        <TrialCard key={t.code} trial={t} onDismiss={() => dismiss(t)} />
      ))}
    </div>
  );
}
